import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { motion } from "motion/react";
import "./Marquee.css";

// Define the logo/image item type
export interface MarqueeItem {
  id: number | string;
  src: string;
  alt: string;
  srcSet?: string;
  sizes?: string;
  loading?: "lazy" | "eager";
}

// Props interface for the Marquee component
interface MarqueeProps {
  items: MarqueeItem[];
  speed?: number;
  direction?: 'left' | 'right' | 'up' | 'down';
  logoWidth?: number;
  logoPadding?: number;
  gap?: number;
  className?: string;
  itemClassName?: string;
  imgClassName?: string;
}

const MarqueeComponent: React.FC<MarqueeProps> = ({
  items,
  speed,
  direction = 'left',
  logoWidth,
  logoPadding,
  gap = 20,
  className = "",
  itemClassName = "",
  imgClassName
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [duplicateCount, setDuplicateCount] = useState(2);
  // Memoize the calculation function
  const calculateDuplicateCount = useCallback(() => {
    if (!containerRef.current) return;

    const containerWidth = containerRef.current.offsetWidth;
    const containerHeight = containerRef.current.offsetHeight;
    const isVertical = direction === 'up' || direction === 'down';

    // Adjust logo width based on container width for better responsiveness
    const calculatedLogoWidth =
      logoWidth ||
      Math.max(
        80, // Minimum logo width
        window.innerWidth <= 350
          ? containerWidth * 0.3 // 30% of container for very small screens
          : window.innerWidth <= 480
            ? containerWidth * 0.25 // 25% of container for mobile
            : window.innerWidth <= 768
              ? 120
              : 150, // Fixed sizes for larger screens
      );

    const calculatedLogoPadding =
      logoPadding ||
      (window.innerWidth <= 480 ? 8 : window.innerWidth <= 768 ? 12 : 15);

    // Calculate copies needed based on container size
    if (isVertical) {
      // For vertical scrolling, calculate based on height
      const itemHeight = 80 + calculatedLogoPadding * 2 + gap; // Assuming 80px height for logos
      const visibleItems = Math.max(1, Math.floor(containerHeight / itemHeight));
      const minCopies = Math.max(2, Math.ceil(8 / visibleItems));
      setDuplicateCount(minCopies);
    } else {
      // For horizontal scrolling, calculate based on width (existing logic)
      const itemWidth = calculatedLogoWidth + calculatedLogoPadding * 2 + gap;
      const visibleItems = Math.max(1, Math.floor(containerWidth / itemWidth));
      const minCopies = Math.max(2, Math.ceil(8 / visibleItems));
      setDuplicateCount(minCopies);
    }
  }, [logoWidth, logoPadding, gap, direction]);

  useEffect(() => {
    calculateDuplicateCount();
    window.addEventListener("resize", calculateDuplicateCount);
    return () => window.removeEventListener("resize", calculateDuplicateCount);
  }, [calculateDuplicateCount]);

  // Memoize duplicatedLogos array
  const duplicatedLogos = useMemo(
    () => Array(duplicateCount).fill(items).flat(),
    [duplicateCount, items],
  );
  return (
    <div 
      className={`marquee-container ${direction === 'up' || direction === 'down' ? 'vertical' : ''} ${className}`} 
      ref={containerRef}
    ><motion.div
        className="marquee-track"
        animate={{
          x: direction === 'left' ? ["0%", "-50%"] : direction === 'right' ? ["-50%", "0%"] : undefined,
          y: direction === 'up' ? ["0%", "-50%"] : direction === 'down' ? ["-50%", "0%"] : undefined
        }}
        transition={{
          x: {
            repeat: Infinity,
            repeatType: "loop",
            duration: speed, // Convert speed to duration
            ease: "linear",
          },
          y: {
            repeat: Infinity,
            repeatType: "loop",
            duration: speed, // Convert speed to duration
            ease: "linear",
          },
        }}
        style={{ gap: `${gap}px` }}
      >
        {duplicatedLogos.map((logo, index) => (
          <div
            key={`${logo.id}-${index}`}
            className={`logo-container h-auto ${itemClassName}`}
            style={{
              padding: `${logoPadding || "inherit"}px`,
            }}
            // data-title={logo.alt}
          >
            <img
              src={logo.src}
              alt={logo.alt}
              srcSet={logo.srcSet}
              sizes={logo.sizes}
              loading={logo.loading}
              className={`logo ${imgClassName}`}
              style={{
                width: logoWidth ? `${logoWidth}px` : undefined,
              }}
            />
          </div>
        ))}

        {duplicatedLogos.map((logo, index) => (
          <div
            key={`${logo.id}-${index}-duplicate`}
            className={`logo-container ${itemClassName}`}
            style={{
              padding: `${logoPadding || "inherit"}px`,
            }}
            data-title={logo.alt}
          >
            <img
              src={logo.src}
              alt={logo.alt}
              srcSet={logo.srcSet}
              sizes={logo.sizes}
              loading={logo.loading}
              className={`logo ${imgClassName}`}
              style={{
                width: logoWidth ? `${logoWidth}px` : undefined,
              }}
            />
          </div>
        ))}
      </motion.div>
    </div>
  );
};

export const Marquee = React.memo(MarqueeComponent);
